import React, { useState, useEffect, useCallback } from 'react';
import ComputerPlayer from './ComputerPlayer.js';

// Hex Game Component
/**
 * Hex: A two-player strategy game on an 11x11 hexagonal grid
 * 
 * Game Rules:
 * - Players alternate placing pieces (Player 1: red, Player 2: blue)
 * - Red player connects top-left to bottom-right edges
 * - Blue player connects top-right to bottom-left edges (left to right on the slanted board)
 * - First player to create a connected path wins
 */
const Hex = () => {
  const [gameStarted, setGameStarted] = useState(false);
  const [playerTypes, setPlayerTypes] = useState({ 1: 'human', 2: 'human' });
  const [board, setBoard] = useState(Array(121).fill(null)); // 11x11 = 121 cells
  const [currentPlayer, setCurrentPlayer] = useState(1); // Player 1 (red) or Player 2 (blue)
  const [winner, setWinner] = useState(null);
  const [computerPlayer] = useState(new ComputerPlayer());
  const [hoveredCell, setHoveredCell] = useState(null);

  /**
   * Convert row and column to board index
   * @param {number} row - Row number (0-10)
   * @param {number} col - Column number (0-10)
   * @returns {number} - Board index
   */
  const coordsToIndex = (row, col) => {
    return row * 11 + col;
  };

  /**
   * Convert board index to row and column
   * @param {number} index - Board index
   * @returns {Object} - {row, col}
   */
  const indexToCoords = (index) => {
    return {
      row: Math.floor(index / 11),
      col: index % 11
    };
  };

  /**
   * Get the neighbors of a hex cell in the slanted grid
   * In a hex grid, each cell has up to 6 neighbors
   * For this slanted layout where each row shifts right:
   * - Same row: left (col-1) and right (col+1)
   * - Row above: same col and col+1
   * - Row below: col-1 and same col
   * @param {number} row - Row number
   * @param {number} col - Column number
   * @returns {Array<number>} - Array of neighbor indices
   */
  const getHexNeighbors = (row, col) => {
    const neighbors = [];
    
    // Left (same row)
    if (col > 0) {
      neighbors.push(coordsToIndex(row, col - 1));
    }
    // Right (same row)
    if (col < 10) {
      neighbors.push(coordsToIndex(row, col + 1));
    }
    // Top-left (row above, same column)
    if (row > 0) {
      neighbors.push(coordsToIndex(row - 1, col));
    }
    // Top-right (row above, column + 1)
    if (row > 0 && col < 10) {
      neighbors.push(coordsToIndex(row - 1, col + 1));
    }
    // Bottom-left (row below, column - 1)
    if (row < 10 && col > 0) {
      neighbors.push(coordsToIndex(row + 1, col - 1));
    }
    // Bottom-right (row below, same column)
    if (row < 10) {
      neighbors.push(coordsToIndex(row + 1, col));
    }
    
    return neighbors;
  };

  /**
   * Check if a player has won by creating a connected path
   * For red (player 1): connect top edge (row 0) to bottom edge (row 10)
   * For blue (player 2): connect left edge (col 0) to right edge (col 10)
   * @param {Array} currentBoard - The current board state
   * @param {number} player - The player to check (1 or 2)
   * @returns {boolean} - True if the player has won
   */
  const checkWin = (currentBoard, player) => {
    const piece = player === 1 ? 'red' : 'blue';
    const visited = new Set();
    const queue = [];

    // Initialize starting positions based on player
    if (player === 1) {
      // Red player: start from top edge (row 0)
      for (let col = 0; col < 11; col++) {
        const idx = coordsToIndex(0, col);
        if (currentBoard[idx] === piece) {
          queue.push(idx);
          visited.add(idx);
        }
      }
    } else {
      // Blue player: start from left edge (col 0)
      for (let row = 0; row < 11; row++) {
        const idx = coordsToIndex(row, 0);
        if (currentBoard[idx] === piece) {
          queue.push(idx);
          visited.add(idx);
        }
      }
    }

    // BFS to find connected path
    while (queue.length > 0) {
      const current = queue.shift();
      const { row, col } = indexToCoords(current);

      // Check if we've reached the goal edge
      if (player === 1 && row === 10) {
        return true; // Red reached bottom edge
      }
      if (player === 2 && col === 10) {
        return true; // Blue reached right edge
      }

      // Explore neighbors
      const neighbors = getHexNeighbors(row, col);
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor) && currentBoard[neighbor] === piece) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    return false;
  };

  /**
   * Handle cell click - attempt to place a piece
   * @param {number} row - Row of clicked cell
   * @param {number} col - Column of clicked cell
   */
  const handleClick = useCallback((row, col) => {
    const index = coordsToIndex(row, col);
    
    if (winner || board[index] !== null) {
      return;
    }

    // Place the piece
    const newBoard = [...board];
    newBoard[index] = currentPlayer === 1 ? 'red' : 'blue';
    setBoard(newBoard);

    // Check if current player has won
    if (checkWin(newBoard, currentPlayer)) {
      setWinner(currentPlayer);
    } else {
      // Switch to next player
      setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    }
  }, [board, currentPlayer, winner]);

  /**
   * Reset the game to initial state
   */
  const resetGame = () => {
    setBoard(Array(121).fill(null));
    setCurrentPlayer(1);
    setWinner(null);
    setGameStarted(false);
  };

  /**
   * Start the game with selected player types
   */
  const startGame = () => {
    setGameStarted(true);
  };

  /**
   * Handle player type selection
   */
  const handlePlayerTypeChange = (player, type) => {
    setPlayerTypes(prev => ({ ...prev, [player]: type }));
  };

  // Handle computer player moves
  useEffect(() => {
    if (!gameStarted || winner) return;

    const isComputerTurn = playerTypes[currentPlayer] === 'computer';

    if (isComputerTurn) {
      // Trigger computer move after a short delay for better UX
      const timeoutId = setTimeout(() => {
        const move = computerPlayer.makeMove(board, currentPlayer);
        if (move !== null) {
          const { row, col } = indexToCoords(move);
          handleClick(row, col);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [currentPlayer, gameStarted, winner, board, playerTypes, computerPlayer, handleClick]);

  /**
   * Get the current game status message
   * @returns {string} - Status message
   */
  const getStatus = () => {
    if (winner) {
      const winnerName = winner === 1 ? 'Red' : 'Blue';
      return `Winner: ${winnerName}!`;
    } else {
      const currentColor = currentPlayer === 1 ? 'Red' : 'Blue';
      return `Current Turn: ${currentColor}`;
    }
  };

  /**
   * Handle SVG cell click by extracting row and col from data attributes
   */
  const handleSVGClick = (event) => {
    const target = event.target;
    if (target.tagName === 'polygon' && target.hasAttribute('data-row')) {
      const row = parseInt(target.getAttribute('data-row'), 10);
      const col = parseInt(target.getAttribute('data-col'), 10);
      handleClick(row, col);
    }
  };

  /**
   * Handle mouse over for hover effects
   */
  const handleSVGMouseOver = (event) => {
    const target = event.target;
    if (target.tagName === 'polygon' && target.hasAttribute('data-row')) {
      const row = parseInt(target.getAttribute('data-row'), 10);
      const col = parseInt(target.getAttribute('data-col'), 10);
      const index = coordsToIndex(row, col);
      if (!winner && board[index] === null) {
        setHoveredCell(index);
      }
    }
  };

  /**
   * Handle mouse out to clear hover effects
   */
  const handleSVGMouseOut = () => {
    setHoveredCell(null);
  };

  /**
   * Render the hex board with pieces
   */
  const renderBoard = () => {
    const SIZE = 11;
    const R = 30;           // circumradius (centre → vertex)
    const GAP = 2;          // gap between cells
    const CELL_R = R - GAP; // drawn hexagon radius
    const BORDER_W = 8;     // coloured border strip width
    const W_HEX = Math.sqrt(3) * R;  // horizontal cell spacing
    const V_STEP = 1.5 * R;          // vertical row spacing
    const PAD = 15;

    const RED_COLOR = '#dc143c';
    const BLUE_COLOR = '#1a5fb4';

    // Pointy-top hex vertex at position k (0 = top, clockwise)
    const hexVertex = (cx, cy, r, k) => {
      const a = (Math.PI / 3) * k - Math.PI / 2;
      return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
    };

    // Polygon points string for a full hexagon
    const hexPoints = (cx, cy, r) =>
      Array.from({ length: 6 }, (_, k) => {
        const v = hexVertex(cx, cy, r, k);
        return `${v.x},${v.y}`;
      }).join(' ');

    // Cell centre for (row, col) in the parallelogram layout
    const cellCx = (row, col) => PAD + BORDER_W + R + col * W_HEX + row * W_HEX * 0.5;
    const cellCy = (row) => PAD + BORDER_W + R + row * V_STEP;

    // Trapezoid along exposed edge k (from vertex k to k+1), extending outward
    const borderTrap = (cx, cy, k) => {
      const v1i = hexVertex(cx, cy, R, k);
      const v2i = hexVertex(cx, cy, R, (k + 1) % 6);
      const v1o = hexVertex(cx, cy, R + BORDER_W, k);
      const v2o = hexVertex(cx, cy, R + BORDER_W, (k + 1) % 6);
      return `${v1i.x},${v1i.y} ${v2i.x},${v2i.y} ${v2o.x},${v2o.y} ${v1o.x},${v1o.y}`;
    };

    // SVG dimensions
    const svgW = Math.ceil(2 * (PAD + BORDER_W + R) + (SIZE - 1 + (SIZE - 1) * 0.5) * W_HEX);
    const svgH = Math.ceil(2 * (PAD + BORDER_W + R) + (SIZE - 1) * V_STEP);

    // ── Border segments ──────────────────────────────────────────────────────
    // Exposed edges per border side (pointy-top hex, rows shift right):
    //   Neighbour (row-1, col)   shares edge 5  → row=0  exposes edge 5
    //   Neighbour (row-1, col+1) shares edge 0  → row=0  exposes edge 0
    //   Neighbour (row+1, col)   shares edge 2  → row=10 exposes edge 2
    //   Neighbour (row+1, col-1) shares edge 3  → row=10 exposes edge 3
    //   Neighbour (row, col-1)   shares edge 4  → col=0  exposes edge 4
    //   Neighbour (row+1, col-1) shares edge 3  → col=0  exposes edge 3
    //   Neighbour (row, col+1)   shares edge 1  → col=10 exposes edge 1
    //   Neighbour (row-1, col+1) shares edge 0  → col=10 exposes edge 0
    // Blue drawn first; red drawn on top (red wins at the two obtuse corners).

    const blueBorders = [];
    const redBorders = [];

    for (let row = 0; row < SIZE; row++) {
      for (let col = 0; col < SIZE; col++) {
        const cx = cellCx(row, col);
        const cy = cellCy(row);

        // Top row → RED edges 5, 0
        if (row === 0) {
          redBorders.push(<polygon key={`rt-${col}-5`} points={borderTrap(cx, cy, 5)} fill={RED_COLOR} />);
          redBorders.push(<polygon key={`rt-${col}-0`} points={borderTrap(cx, cy, 0)} fill={RED_COLOR} />);
        }
        // Bottom row → RED edges 2, 3
        if (row === SIZE - 1) {
          redBorders.push(<polygon key={`rb-${col}-2`} points={borderTrap(cx, cy, 2)} fill={RED_COLOR} />);
          redBorders.push(<polygon key={`rb-${col}-3`} points={borderTrap(cx, cy, 3)} fill={RED_COLOR} />);
        }
        // Left col → BLUE edge 4 always; edge 3 when not bottom row
        if (col === 0) {
          blueBorders.push(<polygon key={`bl-${row}-4`} points={borderTrap(cx, cy, 4)} fill={BLUE_COLOR} />);
          if (row < SIZE - 1) {
            blueBorders.push(<polygon key={`bl-${row}-3`} points={borderTrap(cx, cy, 3)} fill={BLUE_COLOR} />);
          }
        }
        // Right col → BLUE edge 1 always; edge 0 when not top row
        if (col === SIZE - 1) {
          blueBorders.push(<polygon key={`br-${row}-1`} points={borderTrap(cx, cy, 1)} fill={BLUE_COLOR} />);
          if (row > 0) {
            blueBorders.push(<polygon key={`br-${row}-0`} points={borderTrap(cx, cy, 0)} fill={BLUE_COLOR} />);
          }
        }
      }
    }

    // ── Cells & pieces ───────────────────────────────────────────────────────
    const cells = [];
    for (let row = 0; row < SIZE; row++) {
      for (let col = 0; col < SIZE; col++) {
        const cx = cellCx(row, col);
        const cy = cellCy(row);
        const index = coordsToIndex(row, col);
        const piece = board[index];
        const isHovered = hoveredCell === index && !piece && !winner;

        cells.push(
          <g key={`cell-${row}-${col}`}>
            <polygon
              points={hexPoints(cx, cy, CELL_R)}
              fill={isHovered ? '#e8e0d0' : '#f5f0e1'}
              stroke="#b8a88a"
              strokeWidth={1.5}
              data-row={row}
              data-col={col}
              style={{ cursor: !piece && !winner ? 'pointer' : 'default' }}
            />
            {piece === 'red' && (
              <circle cx={cx} cy={cy} r={CELL_R * 0.6} fill={RED_COLOR} stroke="#a01030" strokeWidth={2} pointerEvents="none" />
            )}
            {piece === 'blue' && (
              <circle cx={cx} cy={cy} r={CELL_R * 0.6} fill={BLUE_COLOR} stroke="#0d3a7a" strokeWidth={2} pointerEvents="none" />
            )}
          </g>
        );
      }
    }

    return (
      <div
        className="hex-board"
        onClick={handleSVGClick}
        onMouseOver={handleSVGMouseOver}
        onMouseOut={handleSVGMouseOut}
      >
        <svg viewBox={`0 0 ${svgW} ${svgH}`} xmlns="http://www.w3.org/2000/svg">
          {blueBorders}
          {redBorders}
          {cells}
        </svg>
      </div>
    );
  };

  // Show player configuration screen before game starts
  if (!gameStarted) {
    return (
      <div className="game-container">
        <h1 className="game-title">Hex</h1>
        <h2 className="config-subtitle">Player Configuration</h2>
        <div className="player-config">
          <div className="player-config-item">
            <h3>Player 1 (Red)</h3>
            <div className="player-type-selector">
              <button
                className={`player-type-button ${playerTypes[1] === 'human' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange(1, 'human')}
              >
                Human
              </button>
              <button
                className={`player-type-button ${playerTypes[1] === 'computer' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange(1, 'computer')}
              >
                Computer
              </button>
            </div>
          </div>
          <div className="player-config-item">
            <h3>Player 2 (Blue)</h3>
            <div className="player-type-selector">
              <button
                className={`player-type-button ${playerTypes[2] === 'human' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange(2, 'human')}
              >
                Human
              </button>
              <button
                className={`player-type-button ${playerTypes[2] === 'computer' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange(2, 'computer')}
              >
                Computer
              </button>
            </div>
          </div>
        </div>
        <button className="new-game-button" onClick={startGame}>
          Start Game
        </button>
      </div>
    );
  }

  return (
    <div className="game-container hex-game-container">
      <h1 className="game-title">Hex</h1>
      <div className={`game-status ${winner ? 'winner' : ''}`}>
        {getStatus()}
      </div>
      {renderBoard()}
      <button className="new-game-button" onClick={resetGame}>
        New Game
      </button>
    </div>
  );
};

export default Hex;
